import { useState } from 'react';

import { Money } from '@betterment/core';
import { useGraphQLMutation } from '@betterment/graphql';
import { useNavigate } from 'react-router-dom';

import { ProductCategory, UpdatePurposeInput } from '../../../graphql/schema';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { CompleteDraftPurposeFlowDocument } from '../graphql/mutate.completeDraftPurposeFlow.generated';

type UseCompleteDraftPurposeFlowInput = {
  draftEnvelopeId?: string;
  name: string;
  imageType?: string;
  productCategory?: ProductCategory | null;
};

type PurposeInput = {
  targetAmount?: Money | null;
  purposeSubType: string;
  purposeInput: UpdatePurposeInput;
};

type DisplayableError = {
  __typename: 'DisplayableError';
  path: string[];
  message: string;
};

export const useCompleteDraftPurposeFlow = ({
  draftEnvelopeId,
  name,
  imageType,
  productCategory,
}: UseCompleteDraftPurposeFlowInput) => {
  const navigate = useNavigate();
  const [completeDraftPurposeFlow] = useGraphQLMutation(CompleteDraftPurposeFlowDocument);
  const [errors, setErrors] = useState<DisplayableError[]>([]);

  if (!productCategory) {
    throw new Error('Product category is required to complete the draft purpose flow');
  }

  const completeFlow = async (input: PurposeInput) => {
    const result = await completeDraftPurposeFlow({
      variables: {
        input: {
          draftEnvelopeId,
          name,
          imageType,
          productCategory,
          ...input,
        },
      },
    });

    if (result.data?.completeDraftPurposeFlow?.result?.id) {
      void navigate(
        urlBuilder.appAddGoalFlowsNew({
          advice_type: input.purposeSubType,
          product_category: productCategory.toLowerCase(),
          draft_envelope_id: result.data.completeDraftPurposeFlow.result.id,
        })
      );
    }

    const displayableErrors = result.data?.completeDraftPurposeFlow?.displayableErrors;
    if (displayableErrors && displayableErrors.length > 0) {
      setErrors(displayableErrors);
    }
  };

  return {
    completeFlow,
    errors,
  };
};
